<template>
  <div>
    <tool-bar :title="title"></tool-bar>
    <loading :loading="loading"></loading>

    <error-alert-item
        :error-alert-event="errorAlertEvent"
        :network-error="networkError"
        @resetErrorAlertEvent="errorAlertEvent=false">
    </error-alert-item>

    <activity-redirect-item
        :loan-tasks="tasks"
        :current-loan-task="currentTaskName"
        :is-loan-task="true"
        :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <refresh-item
        :show="showRefreshItem"
        @onRefresh="fetchStageConfigData"
    ></refresh-item>

    <file-upload-item
        ref="fileUploadItem"
        :show-message-on-upload="false"
        @onDocumentUploaded="onDocumentUploaded"
    ></file-upload-item>

    <div v-if="showUI" class="margin_top_40">
      <div
          v-for="[documentCategory, additionalDocumentData] of Object.entries(this.categoryToAdditionalDocumentDataMap)"
          :key="documentCategory"
          class="box-shadow padding-10 margin_top_20 margin-left-10 margin-right-10">

        <weex-text
            class="item-sm-bold-heading"
            v-html="additionalDocumentData.displayText"
        ></weex-text>


        <weex-text
            v-if="additionalDocumentData.optional"
            class="input_label_des border-bottom padding-bottom-5 text-align-center"
            v-html="optionalText"
        ></weex-text>

        <weex-text
            v-if="!additionalDocumentData.optional"
            class="input_label_des border-bottom padding-bottom-5 text-align-center"
            v-html="mandatoryText"
        ></weex-text>

        <wxc-dropdown
            class="margin_top_20"
            title="Document Type"
            default-item="Select"
            :config="dropdownConfig"
            :list="additionalDocumentData.readableDocumentTypes"
            :select-item="getDropdownSelectedItem(documentCategory)"
            @onItemSelected="onDocumentTypeSelected(documentCategory, $event.value)"
        ></wxc-dropdown>

        <file-attach-button
            class="margin-top-10"
            :file-attach-button-id="documentCategory + '_main'"
            text="Upload Document"
            size="big"
            :attach-image-des-src="getAttachImageDesSrc(documentCategory)"
            :disabled="!isDocumentTypeSelected(documentCategory)"
            :enabled-btn-style="attachFileButtonStyle"
            :disabled-button-style="attachFileButtonStyle"
            :enabled-text-style="attachFileButtonTextStyle"
            :disable-text-style="attachFileButtonTextStyle"
            @attachFilesClicked="attachFileClicked(documentCategory)"
            @filesAttached="onFileAttached(documentCategory, $event)">
        </file-attach-button>

        <file-attach-button
            class="margin-top-20 margin-bottom-10 item-cursor"
            :file-attach-button-id="documentCategory + '_more'"
            text="Upload More Documents"
            size="big"
            :attach-image-des-src="imageSrcCameraAdd"
            :disabled="!isDocumentTypeSelected(documentCategory)"
            :enabled-btn-style="additionalButtonStyle"
            :disabled-button-style="additionalButtonStyle"
            :enabled-text-style="additionalButtonTextStyle"
            :disable-text-style="additionalButtonTextStyle"
            @attachFilesClicked="attachFileClicked(documentCategory)"
            @filesAttached="onFileAttached(documentCategory, $event)">
        </file-attach-button>
      </div>

      <wxc-button
          text="Continue"
          class="horizontal_center margin_top_40 margin-bottom-20"
          type="blue"
          @wxcButtonClicked="onContinueClicked"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import ToolBar from "../layout/ToolBar";
import Loading from "../layout/Loading";
import RefreshItem from "../layout/RefreshItem";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import FileAttachButton from "../layout/FileAttachButton";
import StageCommentItem from "../layout/StageCommentItem";
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import WxcDropdown from "../../../weex-ui/packages/wxc-dropdown/index";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import DataSource from "../../data/DataSource";
import {Activities} from "../../utils/ActivityNavigator";
import {MessageConstants} from "../../utils/MessageConstantUtil";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import AttributeUtil, {AttributeDetails} from "../../utils/AttributeUtil";
import AlertService from "../core/alert/AlertService";
import {DefaultResourceConfig, ImageResourcesUtil} from "../../utils/ImageResourcesUtil";
import {EntityType} from "../../utils/EntityType";
import EzUtils from "../../utils/EzUtils";
import DateUtils, {DateFormats} from "../../utils/DateUtils";
import FileUploadItem from "../layout/FileUploadItem";
import LoanTasksUtility from "@/utils/LoanTasksUtility";

const UPLOADED_ADDITIONAL_DOCUMENT_TYPE_OF_ = "uploaded.additional.document.type.of.";

export default {
  name: "AdditionalDocumentsActivity",
  components: {
    ActivityRedirectItem,
    ToolBar,
    Loading,
    ErrorAlertItem,
    FileAttachButton,
    WxcDropdown,
    WxcButton,
    StageCommentItem,
    RefreshItem,
    FileUploadItem
  },

  data: () => ({
    title: "Additional Documents",
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    documentCategories: [],
    categoryToAdditionalDocumentDataMap: {},
    showUI: false,
    showRefreshItem: false
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    showToast(message) {
      AlertService.showToast(message);
    },

    onDocumentTypeSelected(documentCategory, documentType) {
      const additionalDocumentData = this.getAdditionalDocumentData(documentCategory);
      if (additionalDocumentData) {
        const readableDocumentTypeToOriginalDocumentTypeMap =
            additionalDocumentData.readableDocumentTypeToOriginalDocumentTypeMap;

        if (readableDocumentTypeToOriginalDocumentTypeMap
            && readableDocumentTypeToOriginalDocumentTypeMap[documentType]
        ) {
          additionalDocumentData["selectedDocumentType"] =
              readableDocumentTypeToOriginalDocumentTypeMap[documentType];
        }

        this.categoryToAdditionalDocumentDataMap[documentCategory] = additionalDocumentData;
      }
    },

    getDropdownSelectedItem(documentCategory) {
      const additionalDocumentData = this.getAdditionalDocumentData(documentCategory);
      if (additionalDocumentData) {
        if (additionalDocumentData.selectedDocumentType) {
          return this.getReadableDocumentType(additionalDocumentData.selectedDocumentType);
        } else if (additionalDocumentData.readableDocumentTypes
            && additionalDocumentData.readableDocumentTypes.length === 1
        ) {
          return additionalDocumentData.readableDocumentTypes[0];
        }
      }

      return "";
    },

    isDocumentTypeSelected(documentCategory) {
      let documentTypeSelected = false;
      const additionalDocumentData = this.getAdditionalDocumentData(documentCategory);
      if (additionalDocumentData) {
        documentTypeSelected = EzUtils.isNotEmpty(additionalDocumentData.selectedDocumentType);
      }

      return documentTypeSelected;
    },

    isDocumentUploaded(documentCategory) {
      let documentUploaded = false;
      const additionalDocumentData = this.getAdditionalDocumentData(documentCategory);
      if (additionalDocumentData) {
        documentUploaded = additionalDocumentData.documentUploaded;
      }

      return documentUploaded;
    },

    getAttachImageDesSrc(documentCategory) {
      return this.isDocumentUploaded(documentCategory)
          ? this.imageSrcTickGreen
          : this.imageSrcCameraAdd;
    },

    attachFileClicked(documentCategory) {
      if (!this.isDocumentTypeSelected(documentCategory)) {
        this.showToast("Please select Document Type");
      }
    },

    onFileAttached(documentCategory, e) {
      const additionalDocumentData = this.getAdditionalDocumentData(documentCategory);

      if (additionalDocumentData) {
        const file = e.files[0];

        const documentType = additionalDocumentData.selectedDocumentType;

        let entityType = "";
        if (additionalDocumentData.documentTypeToEntityTypeMap) {
          entityType = additionalDocumentData.documentTypeToEntityTypeMap[documentType];
        }
        if (EzUtils.isEmpty(entityType)) {
          entityType = EntityType.CLIENTS;
        }

        const entityId = entityType === EntityType.CLIENTS
            ? LoanScopeDataHolder.getClientId() : LoanScopeDataHolder.getLoanId();

        if (this.$refs && this.$refs.fileUploadItem) {
          this.$refs.fileUploadItem.createDocument(
              entityType,
              entityId,
              documentType,
              `Document uploaded on ${DateUtils.getCurrentTimeAsFormat(
                  DateFormats.YYYY_MM_DD__HH_mm_ss_SSS
              )}`,
              documentType,
              file,
              {
                documentCategory: documentCategory,
                documentType: documentType,
                entityId: entityId,
                entityType: entityType
              }
          );
        }
      }
    },

    onDocumentUploaded(documentUploadData) {
      if (documentUploadData
          && documentUploadData.additionalData
          && documentUploadData.additionalData.documentCategory
          && documentUploadData.additionalData.documentType
      ) {
        this.loading = true;
        const documentCategory = documentUploadData.additionalData.documentCategory;
        const documentType = documentUploadData.additionalData.documentType;
        const entityType = EntityType.LOANS;
        const entityId = LoanScopeDataHolder.getLoanId();
        const attributeDto = AttributeUtil.getAttributeUpdateDto(
            entityType,
            entityId,
            AttributeUtil.getSingleAttributeData(
                AttributeDetails.UPLOADED_ADDITIONAL_DOCUMENT_TYPE_OF.key + documentCategory,
                AttributeDetails.UPLOADED_ADDITIONAL_DOCUMENT_TYPE_OF.name,
                documentType
            )
        );
        DataSource.createOrUpdateAttribute(attributeDto)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          } else {
            this.showToast(MessageConstants.DOCUMENT_UPLOADED);
            const additionalDocumentData = this.getAdditionalDocumentData(documentCategory);
            if (additionalDocumentData) {
              additionalDocumentData.documentUploaded = true;
              this.categoryToAdditionalDocumentDataMap[documentCategory] = additionalDocumentData;
            }
          }
        })
      }
    },

    onContinueClicked() {
      if (this.checkForValidation()) {
        this.completeCurrentTaskAndGetNextTask();
      }
    },

    checkForValidation() {
      if (EzUtils.isNotEmpty(this.documentCategories)) {
        for (let index in this.documentCategories) {
          const documentCategory = this.documentCategories[index];

          const additionalDocumentData = this.getAdditionalDocumentData(documentCategory);
          if (additionalDocumentData
              && !additionalDocumentData.documentUploaded
              && !additionalDocumentData.optional
          ) {
            this.showToast("Please upload " + additionalDocumentData.displayText);
            return false;
          }
        }
      }
      return true;
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
      .then(res => {
        this.loading = false;
        if (res.errorOccurred) {
          this.showError(res.data);
        }
      });
    },

    fetchStageConfigData() {
      this.showRefreshItem = false;
      this.loading = true;

      LoanTasksUtility.getLoanStageConfigData(LoanScopeDataHolder.getLoanId(), this.currentTaskName)
      .then(stageConfigRes => {
        if (stageConfigRes.errorOccurred) {
          this.showError(stageConfigRes.data);
          this.showRefreshItem = true;
        } else {
          if (stageConfigRes.data) {
            const documentCategories = stageConfigRes.data.additionalDocumentCategories
                ? stageConfigRes.data.additionalDocumentCategories : [];
            DataSource.getAttributes(EntityType.LOANS, LoanScopeDataHolder.getLoanId())
            .then(loanAttributesRes => {
              this.loading = false;
              if (loanAttributesRes.errorOccurred) {
                this.showError(loanAttributesRes.data);
                this.showRefreshItem = true;
              } else {
                const loanAttributes = loanAttributesRes.data;
                this.setDocumentRequiredData(loanAttributesRes.data, documentCategories);

                this.showUI = true;
              }
            })
          }
        }
      })
    },

    setDocumentRequiredData(loanAttributes, additionalDocumentCategories) {
      const documentCategories = [];
      const categoryToAdditionalDocumentDataMap = {};

      if (EzUtils.isNotEmpty(additionalDocumentCategories)) {
        additionalDocumentCategories.forEach(additionalDocumentsCategory => {
          const documentCategory = additionalDocumentsCategory["categoryName"];

          let categoryDisplayText = additionalDocumentsCategory["displayText"];
          if (EzUtils.isEmpty(categoryDisplayText)) {
            categoryDisplayText = EzUtils.removeUnderscoreFromString(documentCategory);
          }

          const readableDocumentTypeToOriginalDocumentTypeMap = {};
          const documentTypeToEntityTypeMap = {};
          const originalDocumentTypes = [];
          const readableDocumentTypes = [];

          const allowedDocuments = additionalDocumentsCategory["allowedDocuments"];
          if (EzUtils.isNotEmpty(allowedDocuments)) {
            allowedDocuments.forEach(categoryDocument => {
              const documentType = categoryDocument["documentName"];
              if (EzUtils.isNotEmpty(documentType)) {
                const readableDocumentType = this.getReadableDocumentType(documentType);
                let entityType = categoryDocument["entityType"];
                if (EzUtils.isEmpty(entityType)) {
                  entityType = EntityType.CLIENTS;
                }

                originalDocumentTypes.push(documentType);
                readableDocumentTypes.push(readableDocumentType);
                documentTypeToEntityTypeMap[documentType] = entityType;
                readableDocumentTypeToOriginalDocumentTypeMap[readableDocumentType] = documentType;
              }
            });
          }

          let uploadedDocument = null;

          const attributeDto = this.getAttributeForKey(
              loanAttributes,
              UPLOADED_ADDITIONAL_DOCUMENT_TYPE_OF_ + documentCategory
          );
          if (attributeDto && originalDocumentTypes.includes(attributeDto["value"])) {
            uploadedDocument = attributeDto["value"];
          }

          const additionalDocumentData = {
            categoryName: documentCategory,
            displayText: categoryDisplayText,
            readableDocumentTypes: readableDocumentTypes,
            readableDocumentTypeToOriginalDocumentTypeMap: readableDocumentTypeToOriginalDocumentTypeMap,
            documentTypeToEntityTypeMap: documentTypeToEntityTypeMap,
            selectedDocumentType: uploadedDocument,
            documentUploaded: EzUtils.isNotEmpty(uploadedDocument),
            optional: additionalDocumentsCategory["optional"]
          };

          documentCategories.push(documentCategory);
          categoryToAdditionalDocumentDataMap[documentCategory] = additionalDocumentData;
        });
      }

      this.documentCategories = documentCategories;
      this.categoryToAdditionalDocumentDataMap = categoryToAdditionalDocumentDataMap;
    },

    getReadableDocumentType(documentType) {
      return EzUtils.removeUnderscoreFromString(documentType, "PAN", "ID");
    },

    getAttributeForKey(attributeDtoList, attributeKey) {
      if (EzUtils.isNotEmpty(attributeDtoList) && EzUtils.isNotEmpty(attributeKey)) {
        for (let index in attributeDtoList) {
          const attributeDto = attributeDtoList[index];
          if (attributeKey === attributeDto["key"]) {
            return attributeDto;
          }
        }
      }

      return null;
    },

    getAdditionalDocumentData(documentCategory) {
      if (this.categoryToAdditionalDocumentDataMap && documentCategory) {
        return this.categoryToAdditionalDocumentDataMap[documentCategory];
      }

      return null;
    }
  },

  mounted() {
    this.fetchStageConfigData();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.ADDITIONAL_DOCUMENTS];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
          ? LoanScopeDataHolder.getCurrentLoanTask().name : "";
    },

    dropdownConfig() {
      return DefaultResourceConfig.DROP_DOWN;
    },

    attachFileButtonStyle() {
      return {
        backgroundColor: "#ffffff",
        "border-width": "1px",
        "border-style": "solid",
        "border-color": "gray"
      };
    },

    additionalButtonStyle() {
      return {
        "background-color": "#f5f5f",
        "border-color": "#7f8c8d",
        "border-width": "1px",
        "border-style": "dashed"
      };
    },

    attachFileButtonTextStyle() {
      return {"text-align": "center", color: "#000000", "font-size": "16px"};
    },

    additionalButtonTextStyle() {
      return {"text-align": "center", color: "#7f8c8d", "font-size": "16px"};
    },

    imageSrcCameraAdd() {
      return ImageResourcesUtil.getImageLink(ImageResourcesUtil.CAMERA_ADD);
    },

    imageSrcTickGreen() {
      return ImageResourcesUtil.getImageLink(ImageResourcesUtil.TICK_GREEN);
    },

    optionalText() {
      return "(Optional)";
    },

    mandatoryText() {
      return "(Mandatory)";
    }
  }
};
</script>

<style scoped src="../../assets/style/base.css"></style>
